import React, { useCallback } from "react";
import Drawer from "@ls/react-drawer";
import styled, { css } from "styled-components";
import styles from "./Selection.module.css";
import AsOfDatePicker from "components/AsOfDatePicker";

import EntitySelectorGrid from "components/EntitySelectorGrid";
import EntitySelector from "components/EntitySelector";
import SecuritySelector from "components/SecuritySelector";
import InstrumentTypeFilter from "components/InstrumentTypeFilter"
import TickerTextFilter from "components/TickerTextFilter";
import { AsOfDatePickerStart } from "components/AsOfDatePicker";
import Radio from "@ls/react-radio-switch";

import { toggleDrawer } from "state/app/actions";

import { Flex, FlexBox } from "components/Base";

import { useDispatch, useSelector } from "react-redux";
import { selectIsDrawerOpen } from "state/app/selectors";
import GroupTypeSelector from "components/GroupTypeSelector";
import GroupSelector from "components/GroupSelector";
import DisplayRealizedGlSwitch from "components/DisplayRealizedGlSwitch";

import ReplaceMajorsWithMinorsSwitch from "components/ReplaceMajorsWithMinorsSwitch";

import ShowCanceledTradesSwitch from "components/ShowCanceledTradesSwitch";
import LocalCurrencyFilterSelector from "components/LocalCurrencyFilter";
import OfferingMarketFilter from "components/OfferingMarketFilter";
import TransactionCodeFilter from "components/TransactionCodeFilter";
import TransactionEffectFilter from "components/TransactionEffectFilter";
import AbsMsaaFilter from "components/AbsMsaaFilter";
import MsaaBucketFilter from "components/MsaaBucketFilter";
import TraderFilter from "components/TraderFilter";
import { Cell } from "styled-css-grid";

import GoButton from "components/GoButton";
import { setMode, setInstrumentTypesIncludeOption } from "state/selections/actions";
import Mode, { modeOptions } from "state/selections/Mode";
import SelectionState from "state/selections/SelectionState";
import { selectModeValue } from "state/selections/selectors";

import { selectSelectedInstrumentTypeIncludeOptionValue } from "state/selections/selectors";
import BetaSelector from "components/BetaSelector";
import HoldingAttributesSelector from "../HoldingAttributesSelector";
const OptionsRow = styled(FlexBox)`
  margin: 10px;
`;

const InstrumentTypeRow = styled(OptionsRow)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: 10px;
  align-items: center;
`;

const InstrumentTypeFilterSlot = styled.div`
  min-width: 0;

  & > div {
    max-width: 100%;
    min-width: 0;
  }
`;

const InstrumentTypeRadioSlot = styled.div`
  width: fit-content;
  white-space: nowrap;
  align-self: center;
`;

const Item = styled((props) => <Cell center middle {...props} />)`
  height: unset;
  transition: box-shadow 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  border-radius: 3px;
  align-self: center;
  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12), 0 2px 3px rgba(0, 0, 0, 0.23);
  }
`;

const FitItem = styled(Item)`
  width: fit-content;
  margin: 5px;
`;

const option = css`
  &:not(:first-child) {
    margin-left: 10px;
  }
  & > div {
    height: 100%;
  }
  ${({ maxWidth }) => (maxWidth ? `max-width: min-content;` : ``)}
`;
const FlexOption = styled(Flex)`
  ${option}
  transition: box-shadow 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12), 0 2px 3px rgba(0, 0, 0, 0.23);
  }
  cursor: pointer;
`;

const TopRadio = styled(Cell)`
  display: flex;
  flex-direction: row;
  padding: 0 10px;
  border-radius: 3px;
  width: fit-content;
  background: #fff;
  border: 1px solid #ddd;
  &:hover {
    background: rgba(0, 0, 0, 0.01);
  }
`;
const Label = styled.span`
  font-weight: bold;
  color: rgba(0, 0, 0, 0.85);
  line-height: 1.5;
`;
const Selection = (props) => {
  const dispatch = useDispatch();
  const drawerOpen = useSelector(selectIsDrawerOpen);
  const onClickCloseButton = useCallback(() => dispatch(toggleDrawer()), [
    dispatch,
  ]);
  const selectedModeValue = useSelector(
    selectModeValue(SelectionState.UNCOMMITTED)
  );

  const selectedSelectedInstrumentTypeIncludeOptionValues = useSelector(
    selectSelectedInstrumentTypeIncludeOptionValue(SelectionState.UNCOMMITTED)
  );
  const onChangeMode = useCallback((mode) => dispatch(setMode(mode)), [
    dispatch,
  ]);

  const onChangeInstrumentTypesOption = useCallback((option) => dispatch(setInstrumentTypesIncludeOption(option)), [
    dispatch,
  ]);
  const hideEntitySelectors = React.useMemo(() => {
    return new URLSearchParams(window.location.search)?.get('hideEntitySelectors') === 'true';
  }, [])

  const instrumentTypeIncludeOptions = [
    {
      label: 'Exclude',
      value: false,
    },
    {
      label: 'Include',
      value: true,
    },
  ];

  return (
    <Drawer
      active={drawerOpen}
      onClickCloseButton={onClickCloseButton}
      unmountOnExit={false}
      title="Trade History"
    >
      <div className={styles.root}>
        {!hideEntitySelectors && (
          <OptionsRow>
            <TopRadio width={3}>
              <Label>Entity Source:</Label>
              <Radio
                onChange={onChangeMode}
                options={modeOptions}
                value={selectedModeValue}
                horizontal
              />
            </TopRadio>
            <FlexOption>
              <GoButton />
            </FlexOption>
          </OptionsRow>

        )}
        <OptionsRow>
          <FitItem>
            <AsOfDatePickerStart placeholder="Start Date:" hideSymbolicMode />
          </FitItem>

          <FitItem>
            <AsOfDatePicker placeholder="End Date:" hideSymbolicMode />
          </FitItem>
          {!hideEntitySelectors && (
            <>
              {selectedModeValue === Mode.ACC_GROUPS && (
                <FitItem>
                  <GroupTypeSelector />
                </FitItem>
              )}
            </>
          )}

        </OptionsRow>

        {!hideEntitySelectors && (
          <>
            {selectedModeValue === Mode.ACC_GROUPS && (
              <OptionsRow>           
                  <GroupSelector />       
              </OptionsRow>
            )}
          </>
        )}
        {!hideEntitySelectors && (
          <>
            {selectedModeValue === Mode.ENTITIES && (
              <>
                <OptionsRow>
                  <FlexOption>
                    <EntitySelectorGrid />
                  </FlexOption>
                </OptionsRow>
                <OptionsRow>
                  <EntitySelector />
                </OptionsRow>
              </>
            )}
          </>
        )}
         <OptionsRow>
          <ReplaceMajorsWithMinorsSwitch />
        </OptionsRow>
        {process.env.REACT_APP_HOST_ENV === "ACE" && (
        <>
        <OptionsRow>
          <SecuritySelector />
        </OptionsRow>   
        <InstrumentTypeRow>
          <InstrumentTypeFilterSlot>
            <InstrumentTypeFilter />
          </InstrumentTypeFilterSlot>
          <InstrumentTypeRadioSlot>
            <Radio
              onChange={onChangeInstrumentTypesOption}
              options={instrumentTypeIncludeOptions}
              value={selectedSelectedInstrumentTypeIncludeOptionValues}
              horizontal
            />
          </InstrumentTypeRadioSlot>
        </InstrumentTypeRow>
        <OptionsRow>
          <AbsMsaaFilter />
        </OptionsRow>
        <OptionsRow>
          <MsaaBucketFilter />
        </OptionsRow>
        </>
         )}
        <OptionsRow>
            <LocalCurrencyFilterSelector />
        </OptionsRow>
        <OptionsRow>
          <OfferingMarketFilter />
        </OptionsRow>
        <OptionsRow>
          <TickerTextFilter />
        </OptionsRow>
        <OptionsRow>
          <FlexOption>
            <TransactionEffectFilter />
          </FlexOption>
          <FlexOption>
            <TransactionCodeFilter />
          </FlexOption>
        </OptionsRow>
        {process.env.REACT_APP_HOST_ENV === "ACE" && (
          <OptionsRow>
            <TraderFilter />
          </OptionsRow>
        )}
        <OptionsRow>
          <FlexOption>
            <DisplayRealizedGlSwitch />
          </FlexOption>
          <FlexOption>
            <ShowCanceledTradesSwitch />
          </FlexOption>
        </OptionsRow>
        <OptionsRow>
          <FlexOption>
            <BetaSelector />
          </FlexOption>
        </OptionsRow>
        <OptionsRow>
          <FlexOption>
            <HoldingAttributesSelector />
          </FlexOption>
        </OptionsRow>
        <OptionsRow justify="center">
          <GoButton />
        </OptionsRow>
      </div>
    </Drawer>
  );
};

Selection.propTypes = {};

export default Selection;
